import { arrangeDashboard, getDashboardWidget } from "@repo/shared";
import type { DashboardWidgetKey, DashboardWidgetSize } from "@repo/shared/types";
import { Card } from "@/components/ui/card";
import { useT } from "@/lib/i18n/use-t";
import { cn } from "@/lib/utils";
import { T } from "./layout-preview.i18n";
import { SizeGlyph } from "./size-glyph";
import { useWidgetText } from "./use-widget-text";

const SPAN_WIDTH: Record<number, string> = { 1: "w-1/3", 2: "w-2/3", 3: "w-full" };
const EMPTY_WIDTH: Record<number, string> = { 1: "w-1/3", 2: "w-2/3" };

type LayoutPreviewProps = {
  widgets: DashboardWidgetKey[];
  dashboardName: string;
};

/**
 * The dashboard as blocks, from the same arrangement the dashboard renders —
 * so a gap left by a widget that does not fit shows here before anyone sees
 * it on the dashboard.
 */
export function LayoutPreview({ widgets, dashboardName }: LayoutPreviewProps) {
  const t = useT(T);
  const widgetText = useWidgetText();
  const { keyFigures, sectionRows } = arrangeDashboard(widgets);
  const keyFigureRows: DashboardWidgetKey[][] = [];
  for (let i = 0; i < keyFigures.length; i += 4) keyFigureRows.push(keyFigures.slice(i, i + 4));

  const block = (key: DashboardWidgetKey, label: string, className: string) => {
    const compliance = getDashboardWidget(key).requiredPermission === "compliance";
    return (
      <div
        key={key}
        className={cn(
          "flex items-center justify-center rounded-md border px-1 text-center text-[11px] font-medium",
          compliance
            ? "border-info/30 bg-info/10 text-info"
            : "border-border bg-muted text-muted-foreground",
          className,
        )}
      >
        {label}
      </div>
    );
  };
  const sizeLabel = (size: DashboardWidgetSize) => (size === "full" ? t.full : size);

  return (
    <Card className="space-y-4 p-6">
      <div className="space-y-1">
        <h3 className="font-semibold">{t.layoutPreview}</h3>
        <p className="text-sm text-muted-foreground">{t.previewDescription(dashboardName)}</p>
      </div>

      <div className="space-y-1.5 rounded-lg border bg-muted/30 p-3">
        {keyFigureRows.map((row) => (
          <div key={row.join("|")} className="flex gap-1.5">
            {row.map((key) => block(key, widgetText(key).short, "h-8.5 w-1/4"))}
          </div>
        ))}
        {sectionRows.map((row) => (
          <div key={row.cells.map((c) => c.key).join("|")} className="flex gap-1.5">
            {row.cells.map((cell) =>
              block(
                cell.key,
                `${widgetText(cell.key).name} · ${sizeLabel(getDashboardWidget(cell.key).size)}`,
                cn("h-16", SPAN_WIDTH[cell.span]),
              ),
            )}
            {row.emptyColumns > 0 && (
              <div
                className={cn(
                  "flex h-16 items-center justify-center rounded-md border border-dashed text-[11px] text-muted-foreground",
                  EMPTY_WIDTH[row.emptyColumns],
                )}
              >
                {t.empty}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="space-y-2">
        <div className="flex flex-wrap gap-3.5">
          {(["full", "2/3", "1/3", "1/4"] as const).map((size) => (
            <SizeGlyph key={size} size={size} label={sizeLabel(size)} />
          ))}
        </div>
        <p className="text-xs text-muted-foreground">{t.previewNote}</p>
      </div>
      <div className="flex items-center gap-2 text-xs text-muted-foreground">
        <span className="h-3 w-3 rounded-sm border border-info/30 bg-info/10" />
        {t.complianceLegend}
      </div>
    </Card>
  );
}
